/**
 * site_meta.repository — 현장 메타(표시명·위치·시간 보정) CRUD.
 *
 * Firestore 문서 ID = `${scode}_${hcode}` (ERP 현장 마스터 sales_sites 와 같은 키).
 * 옛 site_aliases / site_locations 를 대체한다 — 두 컬렉션이 현장 문자열을 키로 쓰던 탓에
 * 출하와 영업이 사전을 따로 갖던 문제를 없앤다.
 *
 * 검색·목록은 클라이언트 측에서 (수백 건 규모라 전체 구독으로 충분).
 */
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
  type Unsubscribe,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import { tcol } from "@/domain/tenantPath";
import { coerceSiteMeta, type SiteAddressSource, type SiteDisplaySource, type SiteMeta } from "@/domain/site_meta";
import type { Coord } from "@/services/tmap";

const COL = "site_meta";

/** 표시명만 갱신 */
export interface SiteDisplayInput {
  key: string;
  display: string;
  source: SiteDisplaySource;
  /** ERP 원본 현장명 — 목록·검색 표시용으로 같이 저장 */
  rawName?: string;
  /** 이관·추적용 옛 현장명 (있으면 legacy_names 에 누적) */
  legacyName?: string;
}

/** 위치·시간 보정만 갱신 */
export interface SiteLocationInput {
  key: string;
  coord: Coord | null;
  address?: string;
  travel_min_override?: number | null;
  onsite_min?: number | null;
}

/** doc ID 에서 scode/hcode 를 되살려 항상 같이 저장한다(쿼리·디버깅 편의) */
function codesOf(key: string): { scode: string; hcode: string } {
  const i = key.indexOf("_");
  return i === -1
    ? { scode: key, hcode: "" }
    : { scode: key.slice(0, i), hcode: key.slice(i + 1) };
}

export const siteMetaRepo = {
  async listAll(): Promise<SiteMeta[]> {
    const snap = await getDocs(collection(db, tcol(COL)));
    return snap.docs.map((d) => coerceSiteMeta(d.id, d.data()));
  },

  /** 1건 조회 — 없으면 null */
  async get(key: string): Promise<SiteMeta | null> {
    const k = key.trim();
    if (!k) return null;
    const snap = await getDoc(doc(db, tcol(COL), k));
    return snap.exists() ? coerceSiteMeta(snap.id, snap.data()) : null;
  },

  /** 실시간 구독. onError 로 권한/네트워크 에러 대응 (화면은 규칙 폴백으로 계속 동작) */
  subscribeAll(
    cb: (rows: SiteMeta[]) => void,
    onError?: (err: Error) => void,
  ): Unsubscribe {
    return onSnapshot(
      collection(db, tcol(COL)),
      (snap) => cb(snap.docs.map((d) => coerceSiteMeta(d.id, d.data()))),
      (err) => onError?.(err),
    );
  },

  /** 표시명 upsert — 위치 필드는 건드리지 않는다 */
  async setDisplay(input: SiteDisplayInput): Promise<void> {
    const user = auth.currentUser;
    const key = input.key.trim();
    const prev = input.legacyName ? await this.get(key) : null;
    const legacy = new Set(prev?.legacy_names ?? []);
    if (input.legacyName) legacy.add(input.legacyName);

    await setDoc(
      doc(db, tcol(COL), key),
      {
        ...codesOf(key),
        display: input.display,
        display_source: input.source,
        ...(input.rawName ? { raw_name: input.rawName } : {}),
        ...(legacy.size ? { legacy_names: [...legacy] } : {}),
        edited_by: user?.uid ?? null,
        edited_at: serverTimestamp(),
      },
      { merge: true },
    );
  },

  /** 표시명만 제거 — 위치는 남긴다. 다음 표시부터 규칙 결과로 폴백 */
  async clearDisplay(key: string): Promise<void> {
    await setDoc(
      doc(db, tcol(COL), key.trim()),
      { display: null, display_source: null, edited_at: serverTimestamp() },
      { merge: true },
    );
  },

  /**
   * 주소만 기록 — 좌표·표시명은 건드리지 않는다.
   *
   * `overwrite:false`(기본)면 이미 주소가 있는 문서는 건너뛴다. 지오코딩 캐시(B)가
   * 사람이 확인한 주소를 덮어쓰지 않게 하려는 것이다.
   * 쓰기 권한이 없으면 조용히 실패한다 — 주소 캐시는 부가 기능이라 화면을 막지 않는다.
   */
  async setAddress(
    key: string,
    address: string,
    source: SiteAddressSource,
    overwrite = false,
  ): Promise<boolean> {
    const k = key.trim();
    const addr = address.trim();
    if (!k || !addr) return false;
    try {
      if (!overwrite) {
        const prev = await this.get(k);
        if (prev?.address) return false;
      }
      const user = auth.currentUser;
      await setDoc(
        doc(db, tcol(COL), k),
        {
          ...codesOf(k),
          address: addr,
          address_source: source,
          edited_by: user?.uid ?? null,
          edited_at: serverTimestamp(),
        },
        { merge: true },
      );
      return true;
    } catch {
      return false; // 권한 없음 등 — 부가 기능이므로 무시
    }
  },

  /**
   * 좌표만 확정 — 시간 보정(travel/onsite)·표시명은 건드리지 않는다.
   * 설정 주소를 입력하면 그 주소로 지오코딩해 바로 좌표를 찍는 흐름에서 쓴다.
   */
  async setCoord(key: string, coord: Coord, address?: string): Promise<void> {
    const user = auth.currentUser;
    const k = key.trim();
    await setDoc(
      doc(db, tcol(COL), k),
      {
        ...codesOf(k),
        coord: { lat: coord.lat, lng: coord.lng },
        ...(address ? { address, address_source: "manual" } : {}),
        edited_by: user?.uid ?? null,
        edited_at: serverTimestamp(),
      },
      { merge: true },
    );
  },

  /** 위치·시간 보정 upsert — 표시명은 건드리지 않는다 */
  async setLocation(input: SiteLocationInput): Promise<void> {
    const user = auth.currentUser;
    const key = input.key.trim();
    await setDoc(
      doc(db, tcol(COL), key),
      {
        ...codesOf(key),
        coord: input.coord ? { lat: input.coord.lat, lng: input.coord.lng } : null,
        address: input.address ?? null,
        address_source: input.address ? "manual" : null,
        travel_min_override: input.travel_min_override ?? null,
        onsite_min: input.onsite_min ?? null,
        edited_by: user?.uid ?? null,
        edited_at: serverTimestamp(),
      },
      { merge: true },
    );
  },

  /** 위치만 제거 — 표시명은 남긴다 */
  async clearLocation(key: string): Promise<void> {
    await setDoc(
      doc(db, tcol(COL), key.trim()),
      {
        coord: null, address: null, address_source: null,
        travel_min_override: null, onsite_min: null,
        edited_at: serverTimestamp(),
      },
      { merge: true },
    );
  },

  /** 문서 통째 삭제 */
  async remove(key: string): Promise<void> {
    await deleteDoc(doc(db, tcol(COL), key.trim()));
  },
};
